import { useRef, useState } from "react";
import { Button } from "./ui/button";
import { Download, Share2, X, TrendingDown, TrendingUp, Minus, MessageCircle, Instagram } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { calculateWeightProgress } from "../lib/weightUtils";

export default function ProgressCard({ client, progress, onClose, coachName }) {
  const cardRef = useRef(null);
  const [showShareOptions, setShowShareOptions] = useState(false);

  const sortedProgress = progress && progress.length > 0 
    ? [...progress].sort((a, b) => new Date(a.date) - new Date(b.date))
    : [];
  
  const latestProgress = sortedProgress.length > 0 ? sortedProgress[sortedProgress.length - 1] : null;
  
  const { startWeight, currentWeight, change: weightChange } = calculateWeightProgress(client, progress);
  const percentChange = startWeight > 0 ? ((Math.abs(weightChange) / startWeight) * 100).toFixed(1) : 0;
  
  const startDate = new Date(client.start_date || client.created_at);
  const latestDate = latestProgress ? new Date(latestProgress.date) : new Date();
  const weeksElapsed = Math.max(0, Math.floor((latestDate - startDate) / (1000 * 60 * 60 * 24 * 7)));

  // Body fat change calculation
  const progressWithBF = sortedProgress.filter(p => p.body_fat_percent);
  const latestBF = progressWithBF.length > 0 ? progressWithBF[progressWithBF.length - 1].body_fat_percent : null;
  const firstBF = progressWithBF.length > 0 ? progressWithBF[0].body_fat_percent : null;
  const bfChange = latestBF && firstBF ? parseFloat((latestBF - firstBF).toFixed(1)) : null;
  const lastUpdated = latestProgress ? new Date(latestProgress.date) : null;

  const getGoalLabel = (goal) => {
    const labels = {
      fat_loss: "Fat Loss",
      muscle_gain: "Muscle Gain",
      maintenance: "Maintenance",
      recomp: "Recomposition",
      strength: "Strength",
      general_fitness: "General Fitness",
      competition_prep: "Competition Prep",
      endurance: "Endurance",
    };
    return labels[goal] || goal?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "Fitness";
  };

  const getGoalColors = (goal) => {
    switch (goal) {
      case "fat_loss": return { bg: "rgba(239,68,68,0.15)", text: "#f87171", border: "rgba(239,68,68,0.3)" };
      case "muscle_gain": return { bg: "rgba(34,197,94,0.15)", text: "#4ade80", border: "rgba(34,197,94,0.3)" };
      default: return { bg: "rgba(0,191,255,0.12)", text: "#00BFFF", border: "rgba(0,191,255,0.25)" };
    }
  };

  const isProgressGood = () => {
    if (client.goal === "fat_loss") return weightChange < 0;
    if (client.goal === "muscle_gain") return weightChange > 0;
    return Math.abs(weightChange) < 2;
  };

  const progressColor = isProgressGood() ? "#4ade80" : "#f87171";
  const progressBg = isProgressGood() ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)";
  const progressBorder = isProgressGood() ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)";
  const goalColors = getGoalColors(client.goal);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 1,
        pixelRatio: 3,
        backgroundColor: '#050505',
      });
      const link = document.createElement('a');
      link.download = `${client.name.replace(/\s+/g, '_')}_progress.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Progress card downloaded!");
    } catch (error) {
      toast.error("Failed to download. Try again.");
    }
  };

  const handleShareWhatsApp = () => {
    const changeText = weightChange > 0 ? `+${weightChange.toFixed(1)}` : weightChange.toFixed(1);
    const message = encodeURIComponent(
`Check out my fitness progress!

Goal: ${getGoalLabel(client.goal)}
Start weight: ${startWeight} kg
Current weight: ${currentWeight} kg
Change: ${changeText} kg

Transformation powered by Coach ${coachName || 'FitTrack'}

${window.location.origin}/login`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
    toast.success("Opening WhatsApp...");
  };

  const handleShareInstagram = async () => {
    await handleDownload();
    toast.success("Image downloaded! Open Instagram and share from your gallery.");
  };

  const handleShare = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 1,
        pixelRatio: 3,
        backgroundColor: '#050505',
      });
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], `${client.name}_progress.png`, { type: 'image/png' });
      
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `${client.name}'s Progress`,
          text: `Check out ${client.name}'s fitness transformation! Tracked with FitTrack Coach - ${window.location.origin}/login`,
        });
      } else {
        setShowShareOptions(true);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        setShowShareOptions(true);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="max-w-md w-full my-4">
        {/* Action Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold text-white uppercase tracking-wide">
            Progress Card
          </h3>
          <Button
            size="icon"
            variant="ghost"
            onClick={onClose}
            className="rounded-lg text-white hover:bg-white/10"
            data-testid="close-progress-card"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* ========== EXPORTABLE CARD ========== */}
        <div 
          ref={cardRef} 
          style={{
            background: "linear-gradient(135deg, #050505 0%, #0B0B0F 50%, #050505 100%)",
            borderRadius: "20px",
            padding: "28px",
            border: "1px solid rgba(255,255,255,0.06)",
            fontFamily: "'Barlow Condensed', 'Inter', sans-serif",
            position: "relative",
            overflow: "hidden"
          }}
        >
          {/* Subtle accent glow top-left */}
          <div style={{
            position: "absolute", top: "-60px", left: "-60px",
            width: "200px", height: "200px",
            background: `radial-gradient(circle, ${goalColors.bg} 0%, transparent 70%)`,
            pointerEvents: "none"
          }} />

          {/* Header: Name + Goal + Brand */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px", position: "relative" }}>
            <div>
              <h2 style={{
                fontSize: "28px", fontWeight: 800, color: "#FFFFFF",
                textTransform: "uppercase", letterSpacing: "0.02em", lineHeight: 1.1, margin: 0
              }}>
                {client.name}
              </h2>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "8px" }}>
                <span style={{
                  fontSize: "11px", fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.08em", whiteSpace: "nowrap",
                  padding: "3px 10px", borderRadius: "6px",
                  background: goalColors.bg, color: goalColors.text,
                  border: `1px solid ${goalColors.border}`
                }}>
                  {getGoalLabel(client.goal)}
                </span>
                <span style={{ fontSize: "13px", color: "#A1A1AA", fontWeight: 500, whiteSpace: "nowrap" }}>
                  {weeksElapsed} {weeksElapsed === 1 ? "Week" : "Weeks"} Completed
                </span>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "13px", color: "#D4D4D8", fontWeight: 700, letterSpacing: "0.15em" }}>
                FitTrack
              </div>
              <div style={{ fontSize: "12px", background: "linear-gradient(to right, #00E5FF, #3B82F6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase" }}>
                COACH
              </div>
            </div>
          </div>

          {/* Weight Stats Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "24px" }}>
            {/* Start */}
            <div style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: "14px", padding: "16px", textAlign: "center"
            }}>
              <p style={{ fontSize: "10px", color: "#A1A1AA", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 600, marginBottom: "6px" }}>
                Start
              </p>
              <p style={{ fontSize: "28px", fontWeight: 800, color: "#E4E4E7", lineHeight: 1, margin: "4px 0" }}>
                {startWeight.toFixed(1)}
              </p>
              <p style={{ fontSize: "12px", color: "#A1A1AA", fontWeight: 500 }}>kg</p>
              <p style={{ fontSize: "11px", color: "#A1A1AA", marginTop: "4px", fontWeight: 500 }}>
                {startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
            </div>

            {/* Current */}
            <div style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: "14px", padding: "16px", textAlign: "center"
            }}>
              <p style={{ fontSize: "10px", color: "#A1A1AA", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 600, marginBottom: "6px" }}>
                Current
              </p>
              <p style={{ fontSize: "28px", fontWeight: 800, color: "#FFFFFF", lineHeight: 1, margin: "4px 0" }}>
                {currentWeight.toFixed(1)}
              </p>
              <p style={{ fontSize: "12px", color: "#A1A1AA", fontWeight: 500 }}>kg</p>
              {latestProgress && (
                <p style={{ fontSize: "11px", color: "#A1A1AA", marginTop: "4px", fontWeight: 500 }}>
                  {new Date(latestProgress.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
              )}
            </div>

            {/* Change */}
            <div style={{
              background: progressBg,
              border: `1px solid ${progressBorder}`,
              borderRadius: "14px", padding: "16px", textAlign: "center"
            }}>
              <p style={{ fontSize: "10px", color: "#A1A1AA", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 600, marginBottom: "6px" }}>
                Change
              </p>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
                <p style={{ fontSize: "28px", fontWeight: 800, color: progressColor, lineHeight: 1, margin: "4px 0" }}>
                  {weightChange > 0 ? "+" : ""}{weightChange.toFixed(1)}
                </p>
                {Math.abs(weightChange) >= 0.5 && (
                  weightChange < 0 
                    ? <TrendingDown style={{ width: "16px", height: "16px", color: progressColor }} />
                    : <TrendingUp style={{ width: "16px", height: "16px", color: progressColor }} />
                )}
              </div>
              <p style={{ fontSize: "12px", color: "#D4D4D8", fontWeight: 500 }}>
                kg ({percentChange}%)
              </p>
            </div>
          </div>

          {/* Measurements */}
          {latestProgress && (latestProgress.chest || latestProgress.waist || latestProgress.hips) && (
            <div style={{
              borderTop: "1px solid rgba(255,255,255,0.06)",
              paddingTop: "16px", marginBottom: "16px"
            }}>
              <p style={{ fontSize: "10px", color: "#A1A1AA", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 600, marginBottom: "12px" }}>
                Latest Measurements
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
                {latestProgress.chest && (
                  <div style={{ textAlign: "center" }}>
                    <p style={{ fontSize: "20px", fontWeight: 700, color: "#FFFFFF" }}>{latestProgress.chest}</p>
                    <p style={{ fontSize: "11px", color: "#A1A1AA" }}>Chest (cm)</p>
                  </div>
                )}
                {latestProgress.waist && (
                  <div style={{ textAlign: "center" }}>
                    <p style={{ fontSize: "20px", fontWeight: 700, color: "#FFFFFF" }}>{latestProgress.waist}</p>
                    <p style={{ fontSize: "11px", color: "#A1A1AA" }}>Waist (cm)</p>
                  </div>
                )}
                {latestProgress.hips && (
                  <div style={{ textAlign: "center" }}>
                    <p style={{ fontSize: "20px", fontWeight: 700, color: "#FFFFFF" }}>{latestProgress.hips}</p>
                    <p style={{ fontSize: "11px", color: "#A1A1AA" }}>Hips (cm)</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Body Fat & Weeks Summary */}
          <div style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            paddingTop: "16px", marginBottom: "16px",
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px"
          }}
          data-testid="progress-card-summary-row"
          >
            <div style={{ textAlign: "center" }}>
              <p style={{ fontSize: "10px", color: "#A1A1AA", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 600, marginBottom: "6px" }}>
                Body Fat
              </p>
              {bfChange != null ? (
                <>
                  <p style={{ fontSize: "22px", fontWeight: 800, color: bfChange < 0 ? "#4ade80" : bfChange > 0 ? "#f87171" : "#E4E4E7", lineHeight: 1 }}>
                    {bfChange > 0 ? "+" : ""}{bfChange}%
                  </p>
                  <p style={{ fontSize: "11px", color: "#A1A1AA", marginTop: "2px" }}>change</p>
                </>
              ) : (
                <p style={{ fontSize: "14px", color: "#71717a", fontWeight: 500 }}>-</p>
              )}
            </div>
            <div style={{ textAlign: "center" }}>
              <p style={{ fontSize: "10px", color: "#A1A1AA", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 600, marginBottom: "6px" }}>
                Weeks
              </p>
              <p style={{ fontSize: "22px", fontWeight: 800, color: "#00BFFF", lineHeight: 1 }}>{weeksElapsed}</p>
              <p style={{ fontSize: "11px", color: "#A1A1AA", marginTop: "2px" }}>completed</p>
            </div>
            <div style={{ textAlign: "center" }}>
              <p style={{ fontSize: "10px", color: "#A1A1AA", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 600, marginBottom: "6px" }}>
                Updated
              </p>
              {lastUpdated ? (
                <p style={{ fontSize: "14px", fontWeight: 600, color: "#E4E4E7", lineHeight: 1.3 }}>
                  {lastUpdated.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
              ) : (
                <p style={{ fontSize: "14px", color: "#71717a", fontWeight: 500 }}>-</p>
              )}
            </div>
          </div>

          {/* Footer Branding */}
          <div style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            paddingTop: "16px",
            display: "flex", justifyContent: "space-between", alignItems: "flex-end"
          }}>
            <div style={{ fontSize: "12px", color: "#A1A1AA", fontWeight: 500 }}>
              {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{ fontSize: "12px", color: "#A1A1AA", fontWeight: 500 }}>
                Tracked with{" "}
                <span style={{ color: "#00BFFF", fontWeight: 700 }}>FitTrack Coach</span>
              </p>
              <p style={{ fontSize: "11px", color: "#71717A", fontWeight: 500, marginTop: "2px" }}>
                app.fittrackcoach.in
              </p>
              <p style={{ fontSize: "10px", color: "#A1A1AA", fontWeight: 500, marginTop: "1px" }}>
                by InfiNest Tech Pvt. Ltd.
              </p>
            </div>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={handleDownload}
              variant="outline"
              className="h-12 rounded-xl bg-white/5 border-zinc-700 hover:bg-white/10"
              data-testid="download-jpg-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button
              onClick={handleShare}
              className="h-12 rounded-xl btn-premium text-white"
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>

          {showShareOptions && (
            <div className="grid grid-cols-2 gap-3 animate-in fade-in">
              <Button
                onClick={handleShareWhatsApp}
                className="h-12 rounded-xl bg-green-600 hover:bg-green-700 text-white"
                data-testid="share-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
              <Button
                onClick={handleShareInstagram}
                className="h-12 rounded-xl bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white hover:opacity-90"
                data-testid="share-instagram-btn"
              >
                <Instagram className="w-4 h-4 mr-2" />
                Instagram
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
